import { Info } from 'lucide-react'
import { rulesSection, type RulesSectionId } from './content'
import { useRulesSheet } from './rulesContext'
import './rules.css'

/**
 * The contextual ⓘ. A 16×16 glyph in `currentColor`, muted by default and matching whatever
 * small label it annotates where that label is a different grey (the sharpest week eyebrow is
 * tertiary), inside a 44×44 tap target — the mark is small because it must never compete with
 * the thing it annotates, and the target is big because a 16px hit area on a phone is a miss.
 *
 * Two rules govern where this may appear, and between them the deadline countdown and the
 * profile's sharpest week are the only places in the app that qualify:
 *
 * 1. **Never beside a display title.** A 16px glyph next to a Montserrat 800 heading is an optical
 *    scale mismatch. The ⓘ only ever sits next to a small label at matching weight.
 * 2. **Never beside a control that already explains itself.** The chip circles and the Intuition
 *    Rating label each open a stateful modal on tap; a second "explain this" target on the same
 *    row is the failure mode. Those modals carry a "See all rules" row instead.
 *
 * The sharpest week card taps through to a gameweek's results, which is a destination and not an
 * explanation, so it fails neither rule — the ⓘ is what says why the number is picked the way it
 * is. It sits outside the card's own tap target rather than inside it: a button
 * nested in a button is invalid markup.
 */
export default function RulesInfoButton({
  section,
  className,
}: {
  section: RulesSectionId
  /** Positioning, and the label's colour where it differs — never size, which is the spec above. */
  className?: string
}) {
  const { open } = useRulesSheet()

  return (
    <button
      type="button"
      className={`rules-info ${className ?? ''}`}
      onClick={() => open(section)}
      aria-label={`How ${rulesSection(section).title.toLowerCase()} works`}
    >
      <Info className="rules-info__glyph" aria-hidden="true" />
    </button>
  )
}
